import React, { useState, useMemo } from 'react';
import { Moon } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { SelectField } from '../../components/calculators/FormFields';
import { EmptyResult, PrimaryResult } from '../../components/calculators/ResultCard';
import { sleepCyclesFromWake, sleepCyclesFromBed } from '../../lib/calculatorUtils';

const SleepCalculatorPage = () => {
  const [mode, setMode] = useState('wake');
  const [time, setTime] = useState('07:00');

  const result = useMemo(() => {
    if (!time || !/^\d{1,2}:\d{2}$/.test(time)) return null;
    return mode === 'wake' ? sleepCyclesFromWake(time) : sleepCyclesFromBed(time);
  }, [mode, time]);

  const inputs = (
    <div className="space-y-5" data-testid="sleep-inputs">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Sleep Inputs</h2>
      <p className="text-slate-400 text-sm mb-5">Sleep happens in 90-min cycles. We add 14 minutes for falling asleep.</p>
      <SelectField id="sleep-mode" label="Mode" value={mode} onChange={setMode} options={[
        { value: 'wake', label: 'I want to wake up at…' },
        { value: 'bed', label: 'I am going to bed at…' }
      ]} testId="sleep-mode-select" />
      <div>
        <label htmlFor="sleep-time" className="block text-sm font-medium text-slate-300 mb-1.5">{mode === 'wake' ? 'Wake Up Time' : 'Bed Time'}</label>
        <input
          id="sleep-time"
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className="w-full bg-slate-800/70 border border-slate-700 focus:border-blue-500 focus:outline-none rounded-xl px-4 py-2.5 text-white text-sm"
          data-testid="sleep-time-input"
        />
      </div>
    </div>
  );

  const resultNode = result?.length ? (
    <div className="flex flex-col h-full">
      <PrimaryResult
        label={mode === 'wake' ? `Best bedtimes to wake at ${time}` : `Best wake times if you sleep at ${time}`}
        value={time}
        accent="from-indigo-500 to-violet-500"
        testId="sleep-result-header"
      />
      <div className="mt-8 space-y-2">
        {result.map((r, i) => (
          <div key={i} className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-4 flex items-center justify-between" data-testid={`sleep-cycle-${r.cycles}`}>
            <div>
              <p className="text-white font-semibold text-sm sm:text-base">{r.cycles} cycles · {r.hours}h sleep</p>
              <p className="text-slate-500 text-xs">{mode === 'wake' ? 'Go to bed at' : 'Wake up at'}</p>
            </div>
            <p className="text-white text-xl sm:text-2xl font-bold tabular-nums">{mode === 'wake' ? r.bedTime : r.wakeTime}</p>
          </div>
        ))}
      </div>
      <p className="text-slate-500 text-xs mt-6 leading-relaxed">
        Waking at the end of a 90-min cycle (instead of mid-cycle) often feels more refreshing. Most adults benefit from 7–9 hours of sleep.
      </p>
    </div>
  ) : (
    <EmptyResult message="Enter a time to see your optimal sleep cycles." />
  );

  return (
    <CalculatorLayout
      slug="sleep" title="Sleep Calculator"
      subtitle="Find the best bedtime or wake time aligned with 90-min sleep cycles for refreshed mornings."
      metaDescription="Sleep Calculator to find optimal bedtime or wake time using 90-min sleep cycles."
      icon={Moon}
      accentGradient="from-indigo-500 to-violet-500" accentColor="text-indigo-400"
      badgeBg="bg-indigo-500/10" badgeBorder="border-indigo-500/30"
      inputs={inputs} result={resultNode}
      formulaText={<>
        <p>Sleep occurs in ~90-minute cycles. Waking between cycles minimises grogginess.</p>
        <p>We add a 14-minute buffer for falling asleep.</p>
      </>}
    />
  );
};

export default SleepCalculatorPage;
